import React, { useState } from 'react';
import { Box, Grid, InputLabel, Popover, Typography, Button, TextField, FormControlLabel, Checkbox } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Formik, Form } from 'formik';
import CreateIcon from '@mui/icons-material/Create';

interface DateComponentProps {
  values: any;
  index: number;
  childIndex: number;
  setFieldValue: (key: string, value: any) => void;
}

export default function DateComponent({ values, index, childIndex, setFieldValue }: DateComponentProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const id = open ? 'edit-date-popover' : undefined;

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  // const field = values.fields[index];
  const child = values.fields[index].children[childIndex];
  const value = child.value || null;
 

  return (
    <Box>
      <Grid container alignItems="center" spacing={1}>
        <Grid item xs={11}>
          <InputLabel sx={{ color: 'black', display: 'flex', alignItems: 'center', gap: 1 }}>
            {child.question || 'Select Date'}
            <CalendarMonthIcon fontSize="medium" sx={{ color: 'primary.main' }} />
          </InputLabel>
        </Grid>
        <Grid item xs={1}>
          <Box sx={{ cursor: 'pointer', color: 'primary.main' }} onClick={handleOpen}>
            <CreateIcon fontSize="medium" />
          </Box>
        </Grid>
      </Grid>

      {/* DatePicker rendered outside the popover */}
      <Box mt={1}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            value={value}
            onChange={(newDate) => setFieldValue(`fields.${index}.value`, newDate)}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </LocalizationProvider>
      </Box>

      {/* Popover for editing the label only */}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        PaperProps={{ sx: { width: 300, p: 2 } }}
      >
        <Formik
          initialValues={{
            label: child.label || child.question || '',
            isTimeRequired: child.settings?.isTimeRequired || false,
            includeTimezone: child.settings?.includeTimezone !== false, // default to true
            isStartDate: child.settings?.isStartDate || false,
            isDueDate: child.settings?.isDueDate || false
          }}
          enableReinitialize
          onSubmit={(formValues) => {
            setFieldValue(`fields.${index}.children.${childIndex}.label`, formValues.label);
            setFieldValue(`fields.${index}.children.${childIndex}.settings.isTimeRequired`, formValues.isTimeRequired);
            setFieldValue(`fields.${index}.children.${childIndex}.settings.includeTimezone`, formValues.includeTimezone);
            setFieldValue(`fields.${index}.children.${childIndex}.settings.isStartDate`, formValues.isStartDate);
            setFieldValue(`fields.${index}.children.${childIndex}.settings.isDueDate`, formValues.isDueDate);
            handleClose();
          }}
        >
          {({ values: formValues, handleChange }) => (
            <Form>
              <Typography variant="subtitle1" mb={2}>
                Edit Date Field Settings
              </Typography>
              <TextField
                fullWidth
                name="label"
                value={formValues.label}
                onChange={handleChange}
                size="small"
                placeholder="Enter label"
                sx={{ mb: 2 }}
              />
              <Box>
                <FormControlLabel
                  control={<Checkbox name="isTimeRequired" checked={formValues.isTimeRequired} onChange={handleChange} />}
                  label="Time Required"
                />
              </Box>
              <Box>
                <FormControlLabel
                  control={<Checkbox name="includeTimezone" checked={formValues.includeTimezone} onChange={handleChange} />}
                  label="Include Timezone"
                />
              </Box>
              <Box>
                <FormControlLabel
                  control={<Checkbox name="isStartDate" checked={formValues.isStartDate} onChange={handleChange} />}
                  label="Start Date"
                />
              </Box>
              <Box>
                <FormControlLabel
                  control={<Checkbox name="isDueDate" checked={formValues.isDueDate} onChange={handleChange} />}
                  label="Due Date"
                />
              </Box>
              <Box mt={2} display="flex" justifyContent="flex-end">
                <Button type="submit" size="small" variant="contained">
                  Save
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </Popover>
    </Box>
  );
}
